import { useRef, useCallback, useState, useEffect } from 'react';

// Debounce function
export function debounce<F extends (...args: any[]) => any>(func: F, wait: number): (...args: Parameters<F>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<F>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

// Hook for debounced API calls
export function useDebouncedApiCall<T extends (...args: any[]) => Promise<any>>(
  apiCall: T, 
  delay: number = 500
): [(...args: Parameters<T>) => Promise<ReturnType<T>>, boolean] {
  const [isLoading, setIsLoading] = useState(false);
  const pendingCall = useRef<Promise<any> | null>(null);
  
  const debouncedCall = useCallback(
    (...args: Parameters<T>): Promise<ReturnType<T>> => {
      return new Promise((resolve, reject) => {
        const executeCall = debounce(() => {
          setIsLoading(true);
          pendingCall.current = apiCall(...args)
            .then(result => {
              setIsLoading(false);
              resolve(result as ReturnType<T>);
              return result;
            })
            .catch(error => {
              setIsLoading(false);
              reject(error);
              throw error;
            });
        }, delay);
        
        executeCall();
      });
    },
    [apiCall, delay]
  );
  
  return [debouncedCall, isLoading];
}

// Cache for API responses
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

const apiCache: Record<string, CacheEntry<any>> = {};
const inFlightRequests: Map<string, Promise<any>> = new Map();

// Utility function for retrying requests with exponential backoff
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
  maxDelay: number = 10000,
  shouldRetry: (error: any) => boolean = (error) => {
    // CRITICAL FIX: Never retry 400 errors - these are client errors that won't be fixed by retrying
    if (error.response?.status === 400) {
      console.log(`Not retrying 400 error: ${error.message}`, error.response?.data);
      return false;
    }
    
    // By default, only retry on rate limiting (429) or connection issues
    return error.response?.status === 429 || 
           error.code === 'ECONNABORTED' ||
           error.message.includes('timeout') ||
           error.message.includes('Network Error');
  }
): Promise<T> {
  let retries = 0;
  let delay = initialDelay;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      // Add explicit logging for all errors
      console.log(`Request error: ${error.message}`, error.response?.data);
      
      // If we've reached max retries or this isn't a retryable error, throw
      if (retries >= maxRetries || !shouldRetry(error)) {
        // Log detailed information about non-retryable errors
        if (!shouldRetry(error)) {
          console.log(`Not retrying error: ${error.message}`, error.response?.data);
        }
        throw error;
      }

      // Log the retry attempt
      console.log(`Request failed, retrying in ${delay}ms (attempt ${retries + 1}/${maxRetries})`);
      
      // Wait for the delay period
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase the delay for next attempt (exponential backoff)
      delay = Math.min(delay * 2, maxDelay);
      retries++;
    }
  }
}

// Function to get or set cached data with in-flight request deduplication
export function withCache<T>(
  key: string,
  fetchFn: () => Promise<T>,
  expiresInMs: number = 30000 // 30 seconds default
): Promise<T> {
  const now = Date.now();
  const cached = apiCache[key];
  
  // Return cached data if it's still valid
  if (cached && now - cached.timestamp < cached.expiresIn) {
    console.log(`Using cached data for ${key}`);
    return Promise.resolve(cached.data);
  }
  
  // Check if there's already an in-flight request for this key
  if (inFlightRequests.has(key)) {
    console.log(`Reusing in-flight request for ${key}`);
    return inFlightRequests.get(key)!;
  }
  
  // Create a new request
  const request = fetchFn().then(data => {
    // Only cache if data is not null or undefined
    if (data !== null && data !== undefined) {
      // Store in cache
      apiCache[key] = {
        data,
        timestamp: now,
        expiresIn: expiresInMs
      };
    } else {
      console.warn(`Not caching null/undefined data for key: ${key}`);
    }
    
    // Remove from in-flight requests
    inFlightRequests.delete(key);
    
    return data;
  }).catch(error => {
    // Remove from in-flight requests on error
    inFlightRequests.delete(key);
    console.error(`Cache fetch error for key ${key}:`, error);
    throw error;
  });
  
  // Store the request
  inFlightRequests.set(key, request);
  
  return request;
}

// Clear cache for a specific key or pattern
export function clearCache(keyOrPattern?: string | RegExp): void {
  if (!keyOrPattern) {
    // Clear all cache
    Object.keys(apiCache).forEach(key => delete apiCache[key]);
    return;
  }
  
  if (typeof keyOrPattern === 'string') {
    // Clear specific key
    delete apiCache[keyOrPattern];
    return;
  }
  
  // Clear by pattern
  Object.keys(apiCache).forEach(key => {
    if (keyOrPattern.test(key)) {
      delete apiCache[key];
    }
  });
}

// Hook for cached API calls with auto-invalidation
export function useCachedApiCall<T>(
  apiCall: () => Promise<T>,
  cacheKey: string,
  dependencies: any[] = [],
  expiresInMs: number = 30000
): [T | null, boolean, Error | null, () => void] {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastRefreshTimeRef = useRef<number>(0);
  const REFRESH_COOLDOWN = 2000; // 2 seconds
  
  // Function to force refresh with cooldown
  const refresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN) {
      console.log(`Skipping refresh - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
      return;
    }
    
    setIsLoading(true);
    lastRefreshTimeRef.current = now;
    clearCache(cacheKey);
    
    withCache<T>(cacheKey, apiCall, expiresInMs)
      .then(result => {
        setData(result);
        setError(null);
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [apiCall, cacheKey, expiresInMs]);
  
  // Fetch data when dependencies change
  useEffect(() => {
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN) {
      console.log(`Skipping initial fetch - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
      return;
    }
    
    setIsLoading(true);
    lastRefreshTimeRef.current = now;
    
    withCache<T>(cacheKey, apiCall, expiresInMs)
      .then(result => {
        if (result !== data) {
          setData(result);
        }
        setError(null);
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [...dependencies, cacheKey]);
  
  return [data, isLoading, error, refresh];
}
